import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, DeleteIcon, Dialog, EditIcon, InfoIcon, SpinnerWithBackground, Text } from '@viapeople/components'
import { useState } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'
import { type Column, GenericTable } from '../../../components/GenericTable'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'
import type { AiSummaryConfig, Cycle, Organization } from '../types'
import { AiSummaryConfigModal } from './ai-summaries/AiSummaryConfigModal'
import { AiSummaryStatusModal } from './ai-summaries/AiSummaryStatusModal'

export const CycleAiSummariesTab = () => {
	const { cycleId } = useParams<{ cycleId: string }>()
	const { cycle: _cycle } = useOutletContext<{ organization: Organization; cycle: Cycle }>()
	const toast = useToast()
	const queryClient = useQueryClient()
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [configIdToEdit, setConfigIdToEdit] = useState<string | null>(null)
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
	const [configToDelete, setConfigToDelete] = useState<string | null>(null)
	const [regeneratingConfigId, setRegeneratingConfigId] = useState<string | null>(null)
	const [isStatusModalOpen, setIsStatusModalOpen] = useState(false)
	const [selectedStatusConfig, setSelectedStatusConfig] = useState<AiSummaryConfig | null>(null)

	const { data: configs, isLoading } = useQuery({
		...trpc.admin.aiSummaries.configs.list.queryOptions({ cycleId: cycleId as string }),
		enabled: !!cycleId,
	})

	const {
		data: configToEdit,
		isLoading: isLoadingConfig,
		error: configError,
	} = useQuery({
		...trpc.admin.aiSummaries.configs.show.queryOptions({
			id: configIdToEdit as string,
		}),
		enabled: !!configIdToEdit && isModalOpen,
		staleTime: 0, // Always fetch fresh data
	})

	const handleCreateClick = () => {
		setConfigIdToEdit(null)
		setIsModalOpen(true)
	}

	const handleEdit = (config: AiSummaryConfig) => {
		setConfigIdToEdit(config.id)
		setIsModalOpen(true)
	}

	const handleDelete = (configId: string) => {
		setConfigToDelete(configId)
		setIsDeleteDialogOpen(true)
	}

	const deleteMutation = useMutation({
		...trpc.admin.aiSummaries.configs.delete.mutationOptions({
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.aiSummaries.configs.list.queryKey({ cycleId: cycleId as string }),
				})
				setIsDeleteDialogOpen(false)
				setConfigToDelete(null)
			},
		}),
	})

	const regenerateAllMutation = useMutation({
		...trpc.admin.aiSummaries.regenerateAll.mutationOptions({
			onSuccess: () => {
				setRegeneratingConfigId(null)
				queryClient.invalidateQueries({
					queryKey: trpc.admin.aiSummaries.configs.list.queryKey({ cycleId: cycleId as string }),
				})
			},
			onError: () => {
				setRegeneratingConfigId(null)
			},
		}),
	})

	const generateMissingMutation = useMutation({
		...trpc.admin.aiSummaries.generateMissing.mutationOptions({
			onSuccess: (data) => {
				setRegeneratingConfigId(null)
				queryClient.invalidateQueries({
					queryKey: trpc.admin.aiSummaries.configs.list.queryKey({ cycleId: cycleId as string }),
				})

				if (data.successful > 0) {
					toast.showToast(`Generated ${data.successful} of ${data.total} summaries`)
				} else if (data.total === data.skipped) {
					toast.showToast('All evaluations already have summaries', 'info')
				} else {
					toast.showToast(`No summaries generated. ${data.failed} evaluations did not meet requirements.`, 'error')
				}
			},
			onError: (error) => {
				setRegeneratingConfigId(null)
				toast.showToast(`Error generating summaries: ${error.message}`, 'error')
			},
		}),
	})

	const handleRegenerateAll = (configId: string) => {
		if (!cycleId) return
		setRegeneratingConfigId(configId)
		regenerateAllMutation.mutate({
			cycleId,
			configId,
		})
	}

	const handleGenerateMissing = (configId: string) => {
		if (!cycleId) return
		setRegeneratingConfigId(configId)
		generateMissingMutation.mutate({
			cycleId,
			configId,
		})
	}

	const handleConfirmDelete = async () => {
		if (!configToDelete) return

		try {
			await deleteMutation.mutateAsync({ id: configToDelete })
		} catch {}
	}

	const handleModalClose = () => {
		setIsModalOpen(false)
		setConfigIdToEdit(null)
		queryClient.invalidateQueries({
			queryKey: trpc.admin.aiSummaries.configs.list.queryKey({ cycleId: cycleId as string }),
		})
	}

	const columns: Column<AiSummaryConfig>[] = [
		{
			field: 'label',
			label: 'Label',
			colSpan: 2,
			sortable: true,
			render: (config: AiSummaryConfig) => (
				<div className='flex flex-col gap-1'>
					<Text variant='small' weight='semibold'>
						{config.label}
					</Text>
					{config.globalPrompt && (
						<Text variant='xsmall' className='text-primary-600 truncate max-w-xs'>
							Global: {config.globalPrompt}
						</Text>
					)}
				</div>
			),
		},
		{
			field: 'rolesConsidered',
			label: 'Roles Considered',
			colSpan: 2,
			render: (config: AiSummaryConfig) => (
				<div className='flex flex-wrap gap-1'>
					{config.rolesConsidered.slice(0, 3).map((role) => (
						<span
							key={role}
							className='inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-neutral-100'>
							{role.replace('_', ' ')}
						</span>
					))}
					{config.rolesConsidered.length > 3 && (
						<span className='inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-neutral-100'>
							+{config.rolesConsidered.length - 3} more
						</span>
					)}
				</div>
			),
		},
		{
			field: 'minCommentThreshold',
			label: 'Thresholds',
			colSpan: 1,
			render: (config: AiSummaryConfig) => (
				<div className='flex flex-col'>
					<Text variant='xsmall'>{config.minCommentThreshold} comments min</Text>
					<Text variant='xsmall'>{config.minCharThreshold} chars min</Text>
				</div>
			),
		},
		{
			field: 'label',
			label: 'Actions',
			colSpan: 3,
			render: (config: AiSummaryConfig) => (
				<div className='flex gap-2'>
					<Button
						size='small'
						variant='tertiary'
						icon={InfoIcon}
						onClick={(e) => {
							e.stopPropagation()
							setSelectedStatusConfig(config)
							setIsStatusModalOpen(true)
						}}
						title='View Status'>
						{' '}
					</Button>
					<Button
						size='small'
						variant='tertiary'
						onClick={(e) => {
							e.stopPropagation()
							handleGenerateMissing(config.id)
						}}
						title="Generate summaries for evaluations that don't have them yet"
						disabled={regeneratingConfigId === config.id}
						isLoading={regeneratingConfigId === config.id && generateMissingMutation.isPending}>
						Generate Missing
					</Button>
					<Button
						size='small'
						variant='tertiary'
						onClick={(e) => {
							e.stopPropagation()
							handleRegenerateAll(config.id)
						}}
						title='Regenerate all existing summaries'
						disabled={regeneratingConfigId === config.id}
						isLoading={regeneratingConfigId === config.id && regenerateAllMutation.isPending}>
						Regenerate All
					</Button>
					<Button
						size='small'
						variant='tertiary'
						icon={EditIcon}
						onClick={(e) => {
							e.stopPropagation()
							handleEdit(config)
						}}
						title='Edit Configuration'>
						Edit
					</Button>
					<Button
						size='small'
						variant='tertiary'
						icon={DeleteIcon}
						onClick={(e) => {
							e.stopPropagation()
							handleDelete(config.id)
						}}
						title='Delete Configuration'>
						Delete
					</Button>
				</div>
			),
		},
	]

	if (isLoading) {
		return <SpinnerWithBackground />
	}

	return (
		<div className='mt-4'>
			<div className='flex justify-between items-center mb-6'>
				<Text variant='large' weight='semibold'>
					AI Summary Configurations
				</Text>
				<Button onClick={handleCreateClick}>Create Configuration</Button>
			</div>

			<div className='bg-white rounded-lg shadow-table'>
				<GenericTable<AiSummaryConfig, never>
					columns={columns}
					data={{
						data: configs || [],
					}}
					idField='id'
					showPagination={false}
					emptyState={{
						title: 'No AI Summary configurations',
						description: 'Create your first AI summary configuration to get started.',
						actionLabel: 'Create AI Summary',
						onAction: handleCreateClick,
					}}
				/>
			</div>

			<AiSummaryConfigModal
				cycleId={cycleId as string}
				config={configToEdit}
				onClose={handleModalClose}
				isOpen={isModalOpen}
				isLoading={isLoadingConfig}
				error={configError}
			/>

			<AiSummaryStatusModal
				isOpen={isStatusModalOpen}
				onClose={() => {
					setIsStatusModalOpen(false)
					setSelectedStatusConfig(null)
				}}
				config={selectedStatusConfig}
				cycleId={cycleId as string}
			/>

			<Dialog
				isOpen={isDeleteDialogOpen}
				onClose={() => {
					if (!deleteMutation.isPending) {
						setIsDeleteDialogOpen(false)
						setConfigToDelete(null)
					}
				}}
				onConfirm={handleConfirmDelete}
				title='Delete AI Summary Configuration'
				message='Are you sure you want to delete this AI Summary configuration? This action cannot be reversed.'
				type='delete'
				confirmLabel={deleteMutation.isPending ? 'Deleting...' : 'Delete'}
				isConfirmLoading={deleteMutation.isPending}
			/>
		</div>
	)
}
